<template>
  <div class="screens-view">
    <a-row justify="space-between" align="middle" style="margin-bottom: 16px">
      <a-col>
        <div>
          <h1>TV Screens Management</h1>
          <p class="subtitle">Manage hotel TV displays for digital signage</p>
        </div>
      </a-col>
      <a-col>
        <a-button type="primary" @click="showAddModal = true">
          <PlusOutlined />
          Add New Screen
        </a-button>
      </a-col>
    </a-row>

    <a-card style="margin-bottom: 16px" :body-style="{ padding: '12px 16px' }">
      <a-space>
        <BankOutlined />
        <span style="font-weight: 500">Company:</span>
        <a-select
          v-model:value="selectedCompanyId"
          :options="companyOptions"
          :loading="loadingCompanies"
          style="min-width: 220px"
          placeholder="Select a company"
          allow-clear
          @change="loadScreens"
        />
      </a-space>
    </a-card>

    <a-divider style="margin-top: 0" />

    <a-row :gutter="16">
      <a-col :span="24">
        <a-card title="Screens" style="margin-bottom: 16px">
          <a-list
            item-layout="horizontal"
            :data-source="screens"
            :loading="loadingScreens"
            v-if="screens.length"
          >
            <a-list-item
              v-for="screen in screens"
              :key="screen.id"
              class="screen-item"
              @click="navigateToScreen(screen.id)"
            >
              <a-list-item-meta>
                <template #avatar>
                  <a-avatar :style="{ backgroundColor: screen.status === 'Online' ? '#52c41a' : '#ff4d4f' }">
                    <DesktopOutlined />
                  </a-avatar>
                </template>
                <template #title>{{ screen.name }}</template>
                <template #description>
                  {{ screen.location || 'No location set' }}
                  <br />
                  <small>
                    Last online: {{ screen.last_online_at ? formatDate(screen.last_online_at) : 'Never' }}
                  </small>
                </template>
              </a-list-item-meta>
              <a-tag :color="statusColor(screen)">{{ statusLabel(screen) }}</a-tag>
              <RightOutlined />
            </a-list-item>
          </a-list>
          <a-empty v-else-if="!loadingScreens" description="No screens yet. Add one to get started." />
        </a-card>
      </a-col>
    </a-row>

    <a-modal
      v-model:visible="showAddModal"
      title="Add New Screen"
      :confirm-loading="saving"
      @ok="handleAddScreen"
      @cancel="resetAddForm"
    >
      <a-form layout="vertical">
        <a-form-item label="Screen Name" required>
          <a-input v-model:value="newScreenName" placeholder="e.g. Lobby Screen" />
        </a-form-item>
        <a-form-item label="Location">
          <a-input v-model:value="newScreenLocation" placeholder="e.g. Main Lobby" />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { BankOutlined, DesktopOutlined, PlusOutlined, RightOutlined } from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { Company, Screen } from '@/models'
import { fetchAllCompanies, fetchCompanies } from '@/services/company'
import { createScreen, fetchScreens } from '@/services/screens'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const auth = useAuthStore()

const selectedCompanyId = ref<string | undefined>(undefined)
const ownerCompanies = ref<Company[]>([])
const allCompanies = ref<Company[]>([])
const loadingCompanies = ref(false)
const loadingScreens = ref(false)
const screens = ref<Screen[]>([])

const showAddModal = ref(false)
const saving = ref(false)
const newScreenName = ref('')
const newScreenLocation = ref('')

const isAdmin = computed(() => auth.isAdmin)
const userOwnedCompanyIds = computed(() =>
  Object.entries(auth.companyMembershipRoles)
    .filter(([, role]) => role === 'owner')
    .map(([id]) => id),
)

const companyOptions = computed(() => {
  const options: Array<{ label: string; value: string; disabled?: boolean }> = []

  if (isAdmin.value && allCompanies.value.length > 0) {
    options.push(...allCompanies.value.map((c) => ({ label: c.name, value: c.id })))
    if (ownerCompanies.value.length > 0) {
      options.push({ label: '— My Companies —', value: '__divider__', disabled: true })
      options.push(...ownerCompanies.value.map((c) => ({ label: c.name, value: c.id })))
    }
  } else {
    options.push(...ownerCompanies.value.map((c) => ({ label: c.name, value: c.id })))
  }

  return options
})

const ONLINE_THRESHOLD_MS = 2 * 60 * 1000

function statusColor(screen: Screen): string {
  if (screen.status === 'Maintenance') return 'orange'
  if (screen.status === 'Offline') return 'volcano'
  if (!screen.last_online_at) return 'volcano'
  const elapsed = Date.now() - new Date(screen.last_online_at).getTime()
  return elapsed < ONLINE_THRESHOLD_MS ? 'green' : 'volcano'
}

function statusLabel(screen: Screen): string {
  if (screen.status === 'Maintenance') return 'Maintenance'
  if (screen.status === 'Offline') return 'Offline'
  if (!screen.last_online_at) return 'Offline'
  const elapsed = Date.now() - new Date(screen.last_online_at).getTime()
  return elapsed < ONLINE_THRESHOLD_MS ? 'Online' : 'Offline'
}

function formatDate(date: string | Date): string {
  const d = new Date(date)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMin = Math.floor(diffMs / 60000)

  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

async function loadScreens() {
  if (!selectedCompanyId.value) {
    screens.value = []
    return
  }
  loadingScreens.value = true
  try {
    screens.value = await fetchScreens(selectedCompanyId.value)
  } catch (e) {
    console.error('Failed to load screens', e)
    message.error('Failed to load screens')
  } finally {
    loadingScreens.value = false
  }
}

const navigateToScreen = (id: string) => {
  router.push(`/screens/${id}`)
}

async function handleAddScreen() {
  if (!newScreenName.value.trim()) {
    message.warning('Screen name is required')
    return
  }
  if (!selectedCompanyId.value) {
    message.warning('Please select a company first')
    return
  }

  saving.value = true
  try {
    await createScreen({
      company_id: selectedCompanyId.value,
      name: newScreenName.value.trim(),
      location: newScreenLocation.value.trim() || undefined,
    })
    message.success('Screen created')
    showAddModal.value = false
    resetAddForm()
    await loadScreens()
  } catch (e) {
    console.error('Failed to create screen', e)
    message.error('Failed to create screen')
  } finally {
    saving.value = false
  }
}

function resetAddForm() {
  newScreenName.value = ''
  newScreenLocation.value = ''
}

async function loadCompanies() {
  await auth.ensureAccessContext()
  if (!auth.isAuthenticated) return

  loadingCompanies.value = true
  try {
    if (isAdmin.value) {
      allCompanies.value = await fetchAllCompanies()
      ownerCompanies.value = await fetchCompanies()
    } else {
      ownerCompanies.value = await fetchCompanies()
    }
  } catch (e) {
    console.error('Failed to load companies', e)
  } finally {
    loadingCompanies.value = false
  }

  const firstOpt = companyOptions.value[0]
  if (firstOpt && !firstOpt.disabled) {
    selectedCompanyId.value = firstOpt.value
  }
}

onMounted(async () => {
  await loadCompanies()
  if (selectedCompanyId.value) {
    await loadScreens()
  }
})
</script>

<style scoped>
.screen-item {
  cursor: pointer;
  transition: all 0.3s ease;
}
.screen-item:hover {
  background-color: var(--bg-layout);
}
.subtitle {
  color: #6c757d;
  margin: 0;
}
</style>
